import React, { useState } from "react";
import { toast } from "sonner";
import { Banknote, Download, FileCheck2 } from "lucide-react";
import * as api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useAction, useData } from "@/hooks/useData";
import { Button, DataTable, Input, Loader, PageHeader, PersonCell, Segmented, StatCard, StatusBadge, TableCard } from "@/components/ui";
import { aed, currentMonth, monthLabel } from "@/lib/format";

const STAGES = [
  { value: "all", label: "All" },
  { value: "Draft", label: "Draft", tone: "slate" },
  { value: "Ready for WPS", label: "Ready", tone: "orange" },
  { value: "Processed", label: "Processed" },
  { value: "Paid", label: "Paid", tone: "green" },
];
const NEXT = { Draft: ["Ready for WPS", "Mark ready"], "Ready for WPS": ["Processed", "Process"], Processed: ["Paid", "Mark paid"] };

export default function WpsPayroll() {
  const { user } = useAuth();
  const [month, setMonth] = useState(currentMonth());
  const [stage, setStage] = useState("all");
  const { data: slips, reload } = useData(() => api.salarySlips.list(user, { month }), [user, month]);
  const { run, pending } = useAction();

  const advance = async (ids, status) => {
    const { ok } = await run(() => Promise.all(ids.map((id) => api.salarySlips.setWpsStatus(id, status))),
      ids.length > 1 ? `${ids.length} records moved to ${status}` : `Marked ${status}`);
    if (ok) reload();
  };

  if (!slips) return <Loader />;

  const total = slips.reduce((s, x) => s + x.net_pay, 0);
  const drafts = slips.filter((s) => s.wps_status === "Draft").map((s) => s.id);
  const rows = stage === "all" ? slips : slips.filter((s) => s.wps_status === stage);
  const count = (v) => (v === "all" ? slips.length : slips.filter((s) => s.wps_status === v).length);

  const columns = [
    { key: "employee", header: "Employee", render: (s) => <PersonCell name={s.employee_name} sub={s.employee_id} /> },
    { key: "ids", header: "Labour card / EID", hide: "xl", render: (s) => <div className="font-mono text-[11px]"><p>{s.labour_card}</p><p className="text-slate-400">{s.emirates_id}</p></div> },
    { key: "bank", header: "Bank", hide: "lg", render: (s) => <div className="text-[11px]"><p className="font-semibold">{s.bank_name}</p><p className="font-mono text-slate-400">•••• {s.iban.slice(-4)}</p></div> },
    { key: "basic", header: "Basic", align: "right", hide: "lg", render: (s) => <span className="text-xs tabular-nums">{aed(s.basic)}</span> },
    { key: "allow", header: "Allowances", align: "right", hide: "xl", render: (s) => <span className="text-xs tabular-nums">{aed(s.housing + s.transport + s.communication + s.other_allowance + s.bonus)}</span> },
    { key: "net", header: "Net (AED)", align: "right", render: (s) => <span className="text-xs font-extrabold text-brand-blue tabular-nums">{aed(s.net_pay)}</span> },
    { key: "status", header: "Status", hide: "md", render: (s) => <StatusBadge status={s.wps_status} /> },
    { key: "action", header: "", align: "right", render: (s) => NEXT[s.wps_status] && (
      <Button size="xs" variant={s.wps_status === "Processed" ? "success" : "default"} disabled={pending} onClick={() => advance([s.id], NEXT[s.wps_status][0])}>
        {NEXT[s.wps_status][1]}
      </Button>
    ) },
  ];

  return (
    <>
      <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
        <StatCard label="Batch Total" value={`${aed(total / 1000, 1)}K`} sub="AED" icon={Banknote} />
        <StatCard label="Employees" value={slips.length} icon={FileCheck2} />
        <StatCard label="Awaiting Payment" value={count("Draft") + count("Ready for WPS") + count("Processed")} icon={FileCheck2} color="orange" />
        <StatCard label="Paid" value={count("Paid")} icon={Banknote} color="green" />
      </div>

      <PageHeader
        description="Wage Protection System preview. Move each record from draft to paid."
        actions={<>
          <Input type="month" value={month} max={currentMonth()} onChange={(e) => e.target.value && setMonth(e.target.value)} className="sm:w-44" aria-label="Month" />
          {drafts.length > 0 && <Button disabled={pending} onClick={() => advance(drafts, "Ready for WPS")}>Mark {drafts.length} drafts ready</Button>}
          <Button icon={<Download size={14} />} onClick={() => toast.info("SIF export is on the roadmap")}>Export SIF</Button>
        </>}
      />

      <Segmented value={stage} onChange={setStage} options={STAGES.map((s) => ({ ...s, count: count(s.value) }))} />

      <TableCard heading={`${rows.length} record${rows.length === 1 ? "" : "s"}`} aside={monthLabel(month)}>
        <DataTable columns={columns} rows={rows} empty={{ icon: Banknote, title: "No payroll records", message: "Generate salary slips for this month first." }} />
      </TableCard>
    </>
  );
}
